// src/pages/Home/HomePageVitrine.jsx

import React, { useEffect } from "react";

import useScrollToTop from "../../hooks/useScrollToTop";
import { useUser } from "../../contexts/UserContext";
import AuthIcons from "../../hooks/AuthIcons";
import { saveVisite } from "../../services/visitesServices";
import Reveal from "../../components/ui/Reveal";

import HomeHeroSlider from "./components/HomeHeroSlider";
import ApplicationsShowcase from "./ApplicationsShowcase";
import About from "./About";
import ClubPartners from "./ClubPartners";
import TestimonialsSection from "./TestimonialsSection";
import FormContact from "./FormContact";
import Footer from "./Footer";

function SectionHeader({ kicker, title, text, centered = false }) {
  return (
    <Reveal
      className={centered ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}
    >
      <div className="text-sm font-bold uppercase tracking-[0.18em] text-[#004b86] dark:text-sky-300">
        — {kicker} <span className="text-orange-500">•</span>
      </div>

      <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl">
        {title}
      </h2>

      {text ? (
        <p className="mt-5 text-base leading-8 text-slate-600 dark:text-slate-300 sm:text-lg">
          {text}
        </p>
      ) : null}
    </Reveal>
  );
}

export default function HomePageVitrine() {
  useScrollToTop();

  const { user } = useUser();

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const lastVisitDate = localStorage.getItem("lastVisitDate");

    if (lastVisitDate === today) return;

    const registerVisit = async () => {
      try {
        await saveVisite();
        localStorage.setItem("lastVisitDate", today);
      } catch (error) {
        console.error(
          "Impossible d’enregistrer la visite de la page d’accueil :",
          error,
        );
      }
    };

    registerVisit();
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900 transition-colors dark:bg-slate-950 dark:text-white">
      {!user ? <AuthIcons /> : null}

      <main>
        {/* Introduction immersive de la page */}
        <HomeHeroSlider />

        {/* Présentation des différentes applications */}
        <ApplicationsShowcase />

        {/* Présentation du développeur et de l’accompagnement */}
        <About />

        {/* Illustration de transition */}
        <section
          className="overflow-hidden bg-slate-50 dark:bg-slate-900/40"
          aria-label="Présentation visuelle de MonAppliClub"
        >
          <img
            src="/images/mac_illustration.webp"
            alt="Présentation des outils et applications MonAppliClub"
            className="max-h-[600px] w-full object-cover"
            loading="lazy"
          />

          <div className="border-t border-slate-200 dark:border-white/10" />
        </section>

        {/* Structures déjà accompagnées */}
        <section
          id="clubPartners"
          className="scroll-mt-24 border-y border-slate-200 bg-slate-50 py-16 dark:border-white/10 dark:bg-slate-950 sm:py-20"
        >
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8">
            <ClubPartners />
          </div>
        </section>

        {/* Témoignages clients */}
        <TestimonialsSection />

        {/* Formulaire de contact */}
        <section
          id="contact"
          className="relative scroll-mt-24 overflow-hidden bg-white py-16 dark:bg-slate-950 sm:py-20"
        >
          <div
            className="pointer-events-none absolute inset-0"
            aria-hidden="true"
          >
            <div className="absolute -left-28 top-20 h-80 w-80 rounded-full bg-sky-200/40 blur-3xl dark:bg-sky-500/10" />

            <div className="absolute -right-28 bottom-10 h-80 w-80 rounded-full bg-orange-200/50 blur-3xl dark:bg-orange-500/10" />
          </div>

          <div className="relative mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8">
            <SectionHeader
              kicker="Contact"
              title="Une idée, un besoin, une structure à équiper ?"
              text="Décrivez simplement votre projet. Je vous oriente vers la bonne application ou une solution sur mesure."
            />

            <div className="mt-10">
              <FormContact />
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
}